import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import { FileText, Plus, Eye, Edit, CreditCard, ArrowLeft, Trash2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { formatCurrency } from '../../utils/currency';
import { formatDate } from '../../utils/dateFormat';
import { useOrganization } from '../../hooks/useOrganization';

const InvoiceList = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { organization } = useOrganization();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    fetchInvoices();
    fetchCustomers();
  }, [search, statusFilter, customerFilter, dateFrom, dateTo]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/customers');
      setCustomers(response.data.customers || []);
    } catch (error) {
    }
  };

  const fetchInvoices = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.paymentStatus = statusFilter;
      if (customerFilter) params.customerId = customerFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      
      const response = await axios.get('/api/invoices', { params });
      setInvoices(response.data.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusBadge = (status) => {
    const colors = {
      Unpaid: 'bg-red-100 text-red-800',
      Partial: 'bg-yellow-100 text-yellow-800',
      Paid: 'bg-green-100 text-green-800',
      Overdue: 'bg-red-100 text-red-800'
    };
    return `px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`;
  };

  const handleSelectAll = (checked) => {
    setSelectedItems(checked ? invoices.map(i => i.id) : []);
  };

  const handleSelectItem = (id, checked) => {
    setSelectedItems(prev => 
      checked ? [...prev, id] : prev.filter(item => item !== id)
    );
  };

  const handleBatchDelete = async () => {
    if (!isAdmin || selectedItems.length === 0) return;
    if (!window.confirm(`Delete ${selectedItems.length} invoices?`)) return;
    
    try {
      await Promise.all(selectedItems.map(id => axios.delete(`/api/invoices/${id}`)));
      fetchInvoices();
      setSelectedItems([]);
    } catch (error) {
    }
  };

  const handleBatchPrint = () => {
    if (selectedItems.length === 0) return;
    navigate(`/accounts/invoices/batch-print?ids=${selectedItems.join(',')}`);
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setCustomerFilter('');
    setDateFrom('');
    setDateTo('');
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/accounts')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Accounts
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
        </div>
        <Link
          to="/accounts/invoices/create"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Invoice
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <input
              type="text"
              placeholder="Search invoices..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All Status</option>
              <option value="Unpaid">Unpaid</option>
              <option value="Partial">Partial</option>
              <option value="Paid">Paid</option>
              <option value="Overdue">Overdue</option>
            </select>
            <select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All Customers</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </select>
            <input
              type="date"
              placeholder="From Date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <input
              type="date"
              placeholder="To Date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <button
              onClick={clearFilters}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedItems.length === invoices.length && invoices.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Invoice #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Paid
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(invoice.id)}
                      onChange={(e) => handleSelectItem(invoice.id, e.target.checked)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FileText className="w-4 h-4 text-gray-400 mr-2" />
                      {invoice.invoiceNumber}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {invoice.customer?.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {formatDate(invoice.invoiceDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {formatCurrency(invoice.total, organization?.currency_code)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {formatCurrency(invoice.paidAmount, organization?.currency_code)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getPaymentStatusBadge(invoice.paymentStatus)}>
                      {invoice.paymentStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/accounts/invoices/${invoice.id}`}
                        className="text-blue-600 hover:text-blue-800"
                        title="View"
                      >
                        👁️
                      </Link>
                      {invoice.paymentStatus !== 'Paid' && (
                        <Link
                          to={`/accounts/invoices/${invoice.id}/edit`}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          ✏️
                        </Link>
                      )}
                      {invoice.paymentStatus !== 'Paid' && (
                        <Link
                          to={`/accounts/payments/incoming?invoiceId=${invoice.id}`}
                          className="text-green-600 hover:text-green-800"
                          title="Record Payment"
                        >
                          💳
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {selectedItems.length > 0 && (
        <div className="fixed bottom-4 right-4 bg-white shadow-lg rounded-lg p-4 border">
          <div className="flex items-center gap-4">
            <span className="text-sm">{selectedItems.length} selected</span>
            <button
              onClick={handleBatchPrint}
              className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 flex items-center gap-1"
            >
              <FileText className="w-4 h-4" />
              🖨️ Print with Summary
            </button>
            {isAdmin && (
              <button
                onClick={handleBatchDelete}
                className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 flex items-center gap-1"
              >
                🗑️ Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceList;
